import React, { useContext, useMemo, useState } from 'react';
import { Nav, Navbar } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import { isCustomer, isSystemUser } from '../../helpers/global';
import useAuth from '../../hooks/useAuth';
import LanguagePicker from './LanguagePicker';
import Logo from './Logo';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { ChevronDown, CirclePlus, LogOut } from 'lucide-react';
import AddEditCustomerModal from '../admin/manage-users/customer/AddEditCustomerModal';
import { Button } from '../ui/button';
import { CaretDownFill, Envelope, QuestionCircle, Whatsapp, PersonCircle } from 'react-bootstrap-icons';
import NewDropDown from './NewDropDown';
import { toast } from 'react-toastify';
import { createDefaultStrategies } from '../admin/manage-users/customer/strategies/helper';
import { ENDPOINTS } from '../../helpers/constants';
import { isAdmin } from '../../helpers/global';
import { makeApiRequests } from '../../helpers/api';
import { getFormattedDate } from '../../form-generator/helpers/utility';
import { useParentCustomer } from '../../context/ParentCustomerContext';

const HeaderNavBar = ({ isCustomerPortal, adminCompTable, assigneUsers }) => {
  const { parentCustomer, setParentCustomer } = useParentCustomer();
  const { customers, setCustomers } = useParentCustomer();
  const [customerActionMeta, setCustomerActionMeta] = useState(null);
  const [customerAddingUpdating, setCustomerAddingUpdating] = useState(false);
  const { translate } = useContext(LocalizeContext);
  const { user, logout } = useAuth();
  const role = useMemo(() => user?.role, [user]);
  const history = useHistory();

  const onNewCustomerFormSubmit = async form => {
    const {
      name,
      boardName,
      email,
      phone,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      address,
      status,
      allowedCalculators,
      additionalInfo,
      startDate,
      endDate,
      programType,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      assignedTo,
      crmContact,
      showRujumBoard,
      sendEmail
    } = form;

    if (!name?.trim() && !email?.trim()) {
      return toast.error('Either name or email is required!');
    }

    setCustomerAddingUpdating(true);

    const newCustomer = {
      boardName,
      name,
      email,
      phone,
      role: 'Customer',
      address,
      status,
      allowedCalculators,
      strategies: createDefaultStrategies(),
      additionalInfo,
      startDate,
      endDate,
      programType,
      customerSurvey: level > 1 ? adminCompTable?.customerSurvey : null,
      secondaryName,
      secondaryEmail,
      secondaryPhone,
      level,
      levelTo,
      showGeneralInfo,
      isPublic,
      crmContact,
      showRujumBoard,
      sendEmail,
      deals: [],
      ...(isAdmin(user?.role) ? { assignedTo } : {})
    };

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_BASE,
      requestBody: newCustomer
    });

    setCustomerAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Customer added successfully');
    setCustomerActionMeta(null);
    setParentCustomer(response);
    setCustomers([response, ...customers]);
  };

  const onCustomerDetailChange = customer => {
    setCustomerActionMeta({ ...customerActionMeta, customer });
  };

  const onCustomerSubmit = () => {
    const { customer } = customerActionMeta;
    onNewCustomerFormSubmit(customer);
  };

  const handleNewCustomerClick = () => {
    setCustomerActionMeta({
      mode: 'add',
      customer: {
        startDate: getFormattedDate(new Date()),
        level: 2,
        status: 'New',
        showGeneralInfo: true,
        showRujumBoard: true,
        sendEmail: true
      }
    });
  };

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  return (
    <Navbar className="px-8 w-full flex items-center justify-between border-b shadow-md" bg="white">
      <div className="flex items-center gap-2">
        <Navbar.Brand as="span">
          <Logo noText small dark />
        </Navbar.Brand>
        <div className="flex items-center gap-2 ">
          {role && (
            <>
              {!isCustomer(role) && (
                <Nav.Link to={'/'} as={Link}>
                  <Button size="sm" variant="outline" className=" rounded-full">
                    <span className="text-xs">{translate('rujum')}</span>
                  </Button>
                </Nav.Link>
              )}

              {isSystemUser(role) && (
                <Nav.Link to={'/apartments'} as={Link}>
                  <Button size="sm" variant="outline" className=" rounded-full">
                    <span className="text-xs">{translate('apartments')}</span>
                  </Button>
                </Nav.Link>
              )}

              {isSystemUser(role) && (
                <Nav.Link to={'/admin/comp-table'} as={Link}>
                  <Button size="sm" variant="outline" className=" rounded-full">
                    <span className="text-xs">{translate('comp_table')}</span>
                  </Button>
                </Nav.Link>
              )}

              {!isCustomer(role) && (
                <a
                  href="https://crm.ethica.finance"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button size="sm" variant="outline" className="flex items-center gap-1 rounded-full">
                    <span className="text-xs">CRM</span>
                  </Button>
                </a>
              )}
            </>
          )}
        </div>
      </div>
      {role && isCustomer(role) && (
        <div>
          <NewDropDown
            trigger={
              <Button size="sm" variant="outline" className="flex items-center gap-1">
                <QuestionCircle className="w-4 h-4" />
                <span className="text-xs flex ">
                  {translate('need_help_talk_to_us')}
                  <CaretDownFill className="w-4 h-4 ml-2" />
                </span>
              </Button>
            }
            items={[
              {
                text: translate('email_us'),
                icon: <Envelope className="w-4 h-4" />,
                onClick: () => window.open(`mailto:${process.env.REACT_APP_EMAIL || 'office@ethica.finance'}`)
              },
              {
                text: translate('whatsapp_us'),
                icon: <Whatsapp className="w-4 h-4" />,
                onClick: () => window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '+9720533018468'}`)
              }
            ]}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <AddEditCustomerModal
          show={customerActionMeta !== null}
          onHide={() => setCustomerActionMeta(null)}
          mode={customerActionMeta?.mode || 'add'}
          customer={customerActionMeta?.customer}
          showProgress={customerAddingUpdating}
          user={user}
          users={assigneUsers}
          onChange={onCustomerDetailChange}
          onCustomerSubmit={onCustomerSubmit}
          setParentCustomer={setParentCustomer}
        />
        {!isCustomerPortal && (
          <Button
            size="sm"
            className="flex items-center text-white gap-1 rounded-full"
            onClick={handleNewCustomerClick}
          >
            <CirclePlus size={12} />
            <span className="max-md:hidden">{translate('new_customer')}</span>
          </Button>
        )}
        <LanguagePicker />
        <NewDropDown
          trigger={
            <div className="flex items-center gap-1 cursor-pointer p-2 hover:bg-gray-100 rounded-lg">
              <PersonCircle size={22} />
              <ChevronDown size={16} className="text-gray-500" />
            </div>
          }
          items={[
            {
              text: translate('view_profile'),
              icon: <PersonCircle size={16} />,
              onClick: () => history.push('/profile')
            },
            {
              text: translate('logout'),
              icon: <LogOut size={16} />,
              onClick: handleLogout
            }
          ]}
        />
      </div>
    </Navbar>
  );
};

export default HeaderNavBar;
